import { List, Show, Create, Edit, Datagrid, TextField, DateField, NumberField, ChipField, SimpleForm, TextInput, SelectInput, DateInput, NumberInput, Button, Filter, SearchInput, useListContext, useRecordContext, useDataProvider, useNotify } from 'react-admin';
import { Box, Typography, Paper, Chip, Stepper, Step, StepLabel, Alert, AlertTitle, Grid, IconButton, Divider } from '@mui/material';
import { Add, Delete, CheckCircle, Warning, Close, SwapHoriz } from '@mui/icons-material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ListPageHeader } from '@/components/ListPageHeader';
import type { ChipColor } from '@/types/chip';
import type { JournalEntryResponse } from '@rosta/data-model/application-agnostic/ledger/dtos';

const JOURNAL_STATUS = ['Draft', 'Posted', 'Reversed'] as const;
type JournalStatusValue = (typeof JOURNAL_STATUS)[number];

const STATUS_COLORS: Record<JournalStatusValue, ChipColor> = {
  Draft: 'default',
  Posted: 'success',
  Reversed: 'warning',
};

const formatCurrency = (amount: number | undefined, currency: string = 'ZAR'): string => {
  if (amount === undefined) return '—';
  return `${currency} ${amount.toLocaleString()}`;
};

const formatDate = (iso: string | undefined): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB');
};

export const JournalEntryList = () => (
  <List filters={<JournalFilters />}>
    <JournalEntryListHeader />
    <Datagrid rowClick="show">
      <TextField source="entryNumber" />
      <TextField source="description" />
      <NumberField source="totalDebit" options={{ style: 'currency', currency: 'ZAR' }} />
      <NumberField source="totalCredit" options={{ style: 'currency', currency: 'ZAR' }} />
      <ChipField source="status" />
      <DateField source="entryDate" />
    </Datagrid>
  </List>
);

const JournalEntryListHeader = () => {
  const { data: entriesRaw } = useListContext();
  const typed = entriesRaw as unknown as JournalEntryResponse[] | undefined;
  const total = typed?.length ?? 0;
  const posted = typed?.filter((e) => e.status === 'Posted').length ?? 0;
  const draft = typed?.filter((e) => e.status === 'Draft').length ?? 0;
  const reversed = typed?.filter((e) => e.status === 'Reversed').length ?? 0;
  const totalDebit = typed?.reduce((sum, e) => sum + (e.totalDebit ?? 0), 0) ?? 0;

  return (
    <ListPageHeader
      title="Journal Entries"
      metrics={[
        { label: 'Total', value: String(total), color: 'primary.main' },
        { label: 'Posted', value: String(posted), color: 'success.main' },
        { label: 'Draft', value: String(draft), color: 'info.main' },
        { label: 'Reversed', value: String(reversed), color: 'warning.main' },
      ]}
      pieChartData={[
        { label: 'Posted', value: posted },
        { label: 'Draft', value: draft },
        { label: 'Reversed', value: reversed },
      ].filter((d) => d.value > 0)}
      pieChartTitle="Status"
      barChartData={[totalDebit / 1000000]}
      barChartLabels={['Total Debit (M)']}
      barChartTitle="Volume"
    />
  );
};

export const JournalFilters = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search entry # or description" alwaysOn />
    <SelectInput source="status" choices={JOURNAL_STATUS.map((s) => ({ id: s, name: s }))} />
  </Filter>
);

const JournalEntryShowContent = () => {
  const record = useRecordContext<JournalEntryResponse>();
  const navigate = useNavigate();
  if (!record) return null;

  const isBalanced = record.totalDebit === record.totalCredit;
  const isDraft = record.status === 'Draft';
  const isPosted = record.status === 'Posted';
  const isReversed = record.status === 'Reversed';
  const activeStep = isDraft ? 0 : isPosted ? 1 : 2;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <IconButton onClick={() => navigate('/journal-entries')} size="small" sx={{ color: 'text.secondary' }}>
          <Close />
        </IconButton>
      </Box>

      {!isBalanced && !isDraft && (
        <Alert severity="error" sx={{ mb: 2 }} icon={<Warning />}>
          <AlertTitle>Unbalanced Entry</AlertTitle>
          Total debit ({formatCurrency(record.totalDebit, record.currency)}) does not equal total credit ({formatCurrency(record.totalCredit, record.currency)})
        </Alert>
      )}
      {isReversed && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<SwapHoriz />}>
          <AlertTitle>Entry Reversed</AlertTitle>
          This entry has been reversed by another journal entry
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Entry Number</Typography>
            <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.5 }}>{record.entryNumber}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip label={record.status} size="small" color={STATUS_COLORS[record.status as JournalStatusValue]} />
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Entry Date</Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>{formatDate(record.entryDate)}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Currency</Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>{record.currency}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Description</Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>{record.description}</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Total Debit</Typography>
            <Typography variant="h5" color="error.main" sx={{ mt: 0.5, fontWeight: 700 }}>
              {formatCurrency(record.totalDebit, record.currency)}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Total Credit</Typography>
            <Typography variant="h5" color="success.main" sx={{ mt: 0.5, fontWeight: 700 }}>
              {formatCurrency(record.totalCredit, record.currency)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>Entry Lifecycle</Typography>
        <Stepper activeStep={activeStep} alternativeLabel>
          <Step><StepLabel>Draft</StepLabel></Step>
          <Step><StepLabel>Posted</StepLabel></Step>
          <Step><StepLabel>Reversed</StepLabel></Step>
        </Stepper>
      </Paper>

      {record.metadata?.createdAt && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Audit Trail</Typography>
          <Typography variant="body2" color="text.secondary">
            Created {formatDate(record.metadata.createdAt)} by {record.metadata.createdBy}
          </Typography>
          {record.postedAt && (
            <Typography variant="body2" color="text.secondary">
              Posted {formatDate(record.postedAt)} by {record.postedBy ?? 'system'}
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
};

export const JournalEntryShow = () => (
  <Show>
    <JournalEntryShowContent />
  </Show>
);

export const JournalEntryCreate = () => (
  <Create>
    <SimpleForm>
      <TextInput source="entryNumber" fullWidth required />
      <TextInput source="description" fullWidth required multiline />
      <DateInput source="entryDate" required />
      <TextInput source="reference" fullWidth />
      <SelectInput source="referenceType" choices={[
        { id: 'Invoice', name: 'Invoice' },
        { id: 'Receipt', name: 'Receipt' },
        { id: 'Transaction', name: 'Transaction' },
        { id: 'Manual', name: 'Manual' },
      ]} />
      <NumberInput source="totalDebit" />
      <NumberInput source="totalCredit" />
      <TextInput source="currency" defaultValue="ZAR" />
      <SelectInput source="status" choices={JOURNAL_STATUS.map((s) => ({ id: s, name: s }))} defaultValue="Draft" />
    </SimpleForm>
  </Create>
);

export const JournalEntryEdit = () => (
  <Edit>
    <SimpleForm>
      <TextInput source="entryNumber" fullWidth />
      <TextInput source="description" fullWidth multiline />
      <DateInput source="entryDate" />
      <TextInput source="reference" fullWidth />
      <SelectInput source="referenceType" choices={[
        { id: 'Invoice', name: 'Invoice' },
        { id: 'Receipt', name: 'Receipt' },
        { id: 'Transaction', name: 'Transaction' },
        { id: 'Manual', name: 'Manual' },
      ]} />
      <NumberInput source="totalDebit" />
      <NumberInput source="totalCredit" />
      <TextInput source="currency" />
      <SelectInput source="status" choices={JOURNAL_STATUS.map((s) => ({ id: s, name: s }))} />
    </SimpleForm>
  </Edit>
);
